import React, { FC, useState, useEffect } from "react";
import {
  Container,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgressMolecule } from "../components/molecules/CircularProgressMolecule";
import { VisualizedMenus } from "../components/VisualizedMenus";
import { Toolbar } from "../components/FooterToolbar";
import { FeedbackBar } from "../components/molecules/FeedbackBar";
import { getGpaScore, getGpaScoreDataRef, useAuth0Token } from "../common/http-requests";
import {
  gpaRequestType,
  gpaResponseType,
  gpaDataRefResponseType,
  gpaDataRefRowResponseType,
} from "../common/type";
import { formatSemester } from "../common/util";
import { useTranslation } from 'react-i18next';
import { themeColors } from "../common/colors";
import { useUserId } from "../hooks/useStoreGetters";

// Material-UIのスタイル設定
const useStyles = makeStyles({
  chartContainer: {
    position: 'relative',
    marginTop: '60px',
    padding: '20px',
    overflow: 'hidden'
  },
  tableHeadStyle: {
    textAlign: "center",
    borderRight: "solid 1px #fff",
    lineHeight: "1rem",
    padding: "10px 2px",
  },
  tableCellStyle: {
    position: 'relative',
    padding: "0 2px",
    textAlign: 'center',
    overflow: "hidden",
  },
  tableGpaCellStyle: {
    position: 'relative',
    textAlign: "center",
    padding: "0 2px",
    overflow: "hidden",
    borderRight: "solid 1px #fff",
  },
  tableCellRefStyle: {
    border: 'solid 1px #e2e2e2',
    boxSizing: 'border-box',
  },
  tableCellPsStyle: {
    border: 'solid 1px #bbcddd',
    boxSizing: 'border-box',
  },
  tableCellVertical: {
    writingMode: 'vertical-rl',
    textOrientation: 'upright',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignContent: 'center',
    width: '100%',
    height: '22%',
  },
});

// 初期データ構造
const DefaultGPADataRef: gpaDataRefResponseType = {
  data_ref: [
    {
      user_id: '',
      subject_code: '',
      subject_name: '',
      credit: 0,
      credit_score: 0,
      earned_period: 0,
      earned_semester_code: '',
      credit_grade_point: null
    }
  ],
};

const GPADataRefPage: FC = () => {
  const { t } = useTranslation();
  const [isSending, setIsSending] = useState(true);
  const { getToken } = useAuth0Token();
  const userId = useUserId();
  const classes = useStyles();

  type tableHeadCSType = {
    label: string,
    width: string,
    color: string,
    backgroundColor: string,
  };

  const tableHeadListCS: tableHeadCSType[] = [
    { label: t("common.period"), width: "4vw", color: "white", backgroundColor: themeColors[3] },
    { label: t("common.semester"), width: "4vw", color: "white", backgroundColor: themeColors[3] },
    { label: t("common.subjectName"), width: "20vw", color: "white", backgroundColor: themeColors[3] },
    { label: t("common.credit"), width: "3vw", color: "white", backgroundColor: themeColors[3] },
    { label: t("common.creditScore"), width: "4vw", color: "white", backgroundColor: themeColors[3] },
  ];

  // api取得失敗時のアラート
  const [feedbackInfo, setFeedbackInfo] = useState({
    open: false,
    text: "",
    type: "success" as "error" | "success" | "warning" | "info",
    autoHideDuration: null,
  });

  // clickawayした際、api取得失敗時のアラートを行わない。
  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedbackInfo({ ...feedbackInfo, open: false });
  };

  const [gpaDataRefData, setGpaDataRefData] = useState<gpaDataRefResponseType>(DefaultGPADataRef);
  const [gpaScoreData, setGpaScoreData] = useState<gpaResponseType>({
    scoreList: [],
    seriesNameList: [],
  });

  // 以下は、記録一覧画面のコードを流用した。
  // Phase1と異なり、画面遷移が必要ではないため、location（uselocation）は使用しない。
  // React18(2022年)より、strictMode（javascriptのコードを通常より厳しくエラーチェックできるモード）において
  // オフスクリーンから再度表示されることで、useEffectが２回発火（キック）される仕様変更となっため、1回に制限するよう変更した。
  // また、以下のコードの})();の();は、定義した関数を即時実行している。
  // 最後にreturn文にて定義された関数は、クリーンアップ関数であり、リスナー、オブザーバー等のオブジェクトを設定した場合、
  // レンダリング時に毎回インスタンスを作成されるため、メモリーリーク及びメモリーの圧迫を抑止するための関数。
  // useEffectの依存配列（第2引数）は、通常、空リストもしくは、具体的な変数のリストを登録するのが一般的であり、
  // 定義しない場合は、FCのstate, propsのうちいずれかが更新される度に実行されてしまうため、通常、定義しないことはない。
  let alreadyFiredOnce = false;
  useEffect(() => {
    (async () => {
      if (!alreadyFiredOnce) {
        await getGpaScoreData(userId);
      }
    })();
    return () => {
      alreadyFiredOnce = true;
    };
  }, []);

  const getGpaScoreData = async (
    login_user_id: number,
  ) => {
    setIsSending(true);
    try {
      const requestParams: gpaRequestType = {
        login_user_id,
      };
      const data: gpaResponseType = await getGpaScore(
        await getToken(),
        requestParams
      );
      setGpaScoreData(data);
      const dataRefData: gpaDataRefResponseType = await getGpaScoreDataRef(
        await getToken(),
        requestParams
      );
      setGpaDataRefData(dataRefData);
    } catch {
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: t("msg.cannotLoadData"),
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  const getMyselfValue = (earned_period: number | null, earned_semester_code: string | null): string => {
    if (!earned_period || !earned_semester_code) return '-';
    const items = gpaScoreData.scoreList;
    const index = items.findIndex(item =>
      item.earned_period === earned_period &&
      item.earned_semester_code === earned_semester_code
    );
    return index !== -1 ? String(items[index].myself) : '-';
  };

  let tmpPreviousPeriod: number = 0;
  let tmpPreviousSemester: string = "";

  const hasPeriodOrSemesterChanged = (item: gpaDataRefRowResponseType, index: number): boolean => {
    if (!item.earned_period || !item.earned_semester_code) return false;
    const isDifferent = item.earned_period !== tmpPreviousPeriod || item.earned_semester_code !== tmpPreviousSemester;
    const isFirstRow = index === 0;
    tmpPreviousPeriod = item.earned_period;
    tmpPreviousSemester = item.earned_semester_code;
    return isDifferent && !isFirstRow;
  };

  const tableBodyRow = (item: gpaDataRefRowResponseType, index: number) => {
    return (
      <TableRow key={`table-body-row-${index}`}>
        <React.Fragment key={`${item.subject_code}-${index}`}>
          <TableCell
            className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>
            {item.earned_period}
          </TableCell>
          <TableCell
            className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>
            {formatSemester(item.earned_semester_code, t)}
          </TableCell>
          <TableCell
            className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>
            {item.subject_name}
          </TableCell>
          <TableCell
            className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>
            {item.credit}
          </TableCell>
          <TableCell
            className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}>
            {item.credit_score}
          </TableCell>
          <TableCell sx={{ width: '2px', padding: '0' }} />
          <TableCell
            className={`${classes.tableCellStyle} ${classes.tableCellRefStyle}`}
            sx={{ color: themeColors[0] }}>
            {item.credit_grade_point ?? "－"}
          </TableCell>
        </React.Fragment>
      </TableRow>
    )
  };

  const tableBodyGpaRow = (
    item: gpaDataRefRowResponseType,
    index: number,
    period: number | null,
    semester: string | null
  ) => {
    const displaySemester = formatSemester(semester ?? '', t);
    return (
      <TableRow key={`table-body-gpa-row-${index}`}>
        <React.Fragment key={`${item.subject_code}-${index}`}>
          <TableCell
            className={classes.tableGpaCellStyle}
            sx={{ color: 'white', backgroundColor: themeColors[3] }}
            colSpan={1}>
            {period}
          </TableCell>
          <TableCell
            className={classes.tableGpaCellStyle}
            sx={{ color: 'white', backgroundColor: themeColors[3] }}
            colSpan={1}>
            {displaySemester}
          </TableCell>
          <TableCell
            className={classes.tableGpaCellStyle}
            sx={{ color: 'white', backgroundColor: themeColors[3] }}
            colSpan={3}>
            GPA
          </TableCell>
          <TableCell sx={{ width: '2px', padding: '0' }} />
          <TableCell
            className={classes.tableGpaCellStyle}
            sx={{ width: "10vw", color: 'white', backgroundColor: themeColors[0], fontSize: '.8rem' }}
          >
            {getMyselfValue(period, semester)}
          </TableCell>
        </React.Fragment>
      </TableRow>
    )
  };


  return (
    <Container maxWidth="xl">
      {isSending ?
        <CircularProgressMolecule displayLabel={t("msg.loading")} /> :
        (
          <>
            <VisualizedMenus />
            {feedbackInfo.open &&
              <FeedbackBar
                feedbackInfo={feedbackInfo}
                handleClose={handleClose}
              ></FeedbackBar>
            }
            <Paper className={classes.chartContainer}>
              <TableContainer>
                <Table aria-label="simple table">
                  <TableHead key="table-head">
                    <TableRow>
                      <TableCell
                        className={classes.tableHeadStyle}
                        sx={{ color: 'white', backgroundColor: themeColors[3] }}
                        colSpan={5}
                      >{t("common.subjectName")}</TableCell>
                      <TableCell sx={{ width: '2px', padding: '0' }} />
                      <TableCell
                        className={classes.tableHeadStyle}
                        sx={{ width: "10vw", color: 'white', backgroundColor: themeColors[0], fontSize: '.8rem' }}
                        rowSpan={2}
                        colSpan={1}
                      >Grade Point</TableCell>
                    </TableRow>
                    <TableRow>
                      {tableHeadListCS.map((item: tableHeadCSType, index: number) => (
                        <TableCell
                          key={`table-head-class-list-${index}`}
                          className={classes.tableHeadStyle}
                          sx={{ width: item.width, color: item.color, backgroundColor: item.backgroundColor }}
                          rowSpan={3}
                        >{item.label}
                        </TableCell>
                      ))}
                      <TableCell sx={{ width: '2px', padding: '0' }} />
                    </TableRow>
                  </TableHead>
                  <TableBody key="table-body">
                    {gpaDataRefData.data_ref.map((item: gpaDataRefRowResponseType, index: number) => {
                      const lastRow = index === gpaDataRefData.data_ref.length - 1;
                      const periodOrSemesterChanged = hasPeriodOrSemesterChanged(item, index);
                      const previousPeriod = index === 0 ? null : gpaDataRefData.data_ref[index - 1].earned_period;
                      const previousSemester = index === 0 ? null : gpaDataRefData.data_ref[index - 1].earned_semester_code;
                      return (
                        <React.Fragment key={`table-body-rows-${index}`}>
                          {lastRow ? (
                            <>
                              {tableBodyRow(item, index)}
                              {tableBodyGpaRow(item, index, item.earned_period, item.earned_semester_code)}
                            </>
                          ) : periodOrSemesterChanged ? (
                            <>
                              {tableBodyGpaRow(item, index, previousPeriod, previousSemester)}
                              {tableBodyRow(item, index)}
                            </>
                          ) : (
                            <>
                              {tableBodyRow(item, index)}
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      <Toolbar />
    </Container>
  );
};

export default GPADataRefPage;
